import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight, Database, Globe } from 'lucide-react';

/**
 * Extracted from CategoryConsole.js (component-split refactor, 2026-10-09).
 * Pure presentational — takes only `strategy` (the raw AI response + the
 * internal_factors numeric block) and renders the "why this strategy was
 * generated" collapsible panel.
 */
export function FactorsConsidered({ strategy }) {
  const [open, setOpen] = useState(false);

  // Parse external factors from AI response
  const parseExternalFactors = (text) => {
    if (!text) return [];
    const lines = text.split('\n');
    const factors = [];
    let inSection = false;
    for (const line of lines) {
      if (line.toLowerCase().includes('**external market factors')) { inSection = true; continue; }
      if (inSection && line.startsWith('**') && !line.toLowerCase().includes('external')) break;
      if (inSection && (line.startsWith('- ') || line.startsWith('* '))) {
        factors.push(line.replace(/^[-*]\s*/, '').trim());
      }
    }
    return factors;
  };

  const externalFactors = parseExternalFactors(strategy.strategy);
  const internalFactors = strategy.internal_factors || [];

  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid="factors-considered">
      <CollapsibleTrigger asChild>
        <button className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md bg-muted/50 hover:bg-muted transition-colors text-sm font-medium" data-testid="factors-toggle">
          <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} />
          <Database className="w-4 h-4 text-muted-foreground" />
          Factors Considered in This Strategy
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Internal Factors */}
          <div className="rounded-lg border border-border p-4 space-y-3" data-testid="internal-factors">
            <div className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Database className="w-4 h-4" />
              Internal Factors (From Spend Data)
            </div>
            <div className="space-y-1.5">
              {internalFactors.map((f, i) => (
                <div key={i} className="flex justify-between text-xs py-1 border-b border-border/50 last:border-0">
                  <span className="text-muted-foreground">{f.factor}</span>
                  <span className="font-medium">{f.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* External Factors */}
          <div className="rounded-lg border border-border p-4 space-y-3" data-testid="external-factors">
            <div className="flex items-center gap-2 text-sm font-semibold text-primary">
              <Globe className="w-4 h-4" />
              External Factors (Market Intelligence)
            </div>
            <div className="space-y-1.5">
              {externalFactors.length > 0 ? externalFactors.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-xs py-1">
                  <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  <span>{f}</span>
                </div>
              )) : (
                <p className="text-xs text-muted-foreground italic">External factors included in the strategy above.</p>
              )}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
